import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalFooter, ModalHeader, ModalBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import { confirmAction, confirmDelete } from '../../../utils/confirm';
import notify from '../../../utils/notify';
import '../../TenderOpening/components/TenderOpening.css';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormGrid,
  RequiredNote,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  maxLength,
  required,
  validateForm as runValidation,
} from '../../../Components/Form';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import ListPager from '../../../Components/Common/ListPager';
import {
  EmptyState,
  SkeletonRows,
} from '../../../Components/Common/ListStates';

const schema = {
  positionId: {
    label: 'Position ID',
    rules: [required(), maxLength(100)],
  },
  name: {
    label: 'Position Name',
    rules: [maxLength(255)],
  },
  department: {
    label: 'Department',
    rules: [maxLength(255)],
  },
};

const removedUsersText = count =>
  count > 0
    ? ` ${count} IDAMAN user${count === 1 ? '' : 's'} can no longer sign in.`
    : '';

const readError = async (response, fallback) => {
  const errorData = await response.json().catch(() => ({}));
  return new Error(errorData.message || errorData.errors?.[0] || fallback);
};

const PositionIdamanView = () => {
  const token = getAuthToken();
  const [data, setData] = useState({ items: [] });
  const [modal, setModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Table States
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const initialFormState = {
    id: null,
    positionId: '',
    originalPositionId: '',
    name: '',
    department: '',
    description: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const qs = new URLSearchParams({
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      });

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Search', debouncedSearchText.trim());
      }

      // Tanpa sortField, server mengurutkan dari yang terbaru dibuat.
      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const response = await fetch(
        `${api.API_URL}/position-idaman?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw await readError(
          response,
          `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      setData({ items: result.data?.items || [] });
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      notify.apiError(error, 'load data');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText('');
    setDebouncedSearchText('');
    setCurrentPage(1);
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const toggleModal = () => {
    if (modal) {
      setErrors({});
    }
    setModal(!modal);
  };

  const handleEdit = row => {
    setFormData({
      id: row.id,
      positionId: row.positionId || '',
      originalPositionId: row.positionId || '',
      name: row.name || '',
      department: row.department || '',
      description: row.description || '',
    });
    setModal(true);
  };

  const handleDelete = async row => {
    const confirmed = await confirmDelete('Position ID', row.positionId, {
      message:
        'IDAMAN users with this Position ID are removed and can no longer sign in. Their active sessions end immediately.',
    });
    if (!confirmed) return;

    try {
      const response = await fetch(`${api.API_URL}/position-idaman/${row.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw await readError(response, 'Delete operation failed');
      }

      const result = await response.json().catch(() => ({}));
      notify.success(
        `Position ID deleted successfully.${removedUsersText(result.data?.removedUsers)}`
      );
      fetchData();
    } catch (error) {
      notify.apiError(error, 'delete data');
    }
  };

  // Sync biasanya jalan otomatis tiap hari pukul 05.00 WIB; tombol ini untuk menarik
  // user lebih awal setelah Position ID baru ditambahkan.
  const handleSync = async () => {
    const confirmed = await confirmAction({
      tone: 'primary',
      icon: 'ri-refresh-line',
      title: 'Sync IDAMAN users now?',
      message:
        'IDAMAN users of company 2222 with the listed Position IDs are pulled in the background, and new users get the General role. Users who no longer match are removed and can no longer sign in. This also runs automatically every day at 05:00 WIB.',
      confirmLabel: 'Sync now',
    });
    if (!confirmed) return;

    setIsSyncing(true);
    try {
      const response = await fetch(
        `${api.API_URL}/usermanagement/SyncAllUserIdamanData`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw await readError(response, 'Sync request failed');
      }

      const result = await response.json().catch(() => ({}));
      if (result.success === false) {
        throw new Error(result.message || 'Sync request failed');
      }

      notify.success(
        'IDAMAN user sync started successfully. Refresh in a few minutes to see the user count.'
      );
    } catch (error) {
      notify.apiError(error, 'start the sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const validateForm = () => {
    const tempErrors = runValidation(formData, schema);
    setErrors(tempErrors);
    if (!hasErrors(tempErrors)) return true;
    focusFirstError(tempErrors, ['positionId', 'name', 'department']);
    return false;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const isEdit = !!formData.id;
    const positionIdChanged =
      isEdit &&
      formData.positionId.trim().toLowerCase() !==
        formData.originalPositionId.trim().toLowerCase();
    if (positionIdChanged) {
      const confirmed = await confirmAction({
        tone: 'danger',
        icon: 'ri-error-warning-line',
        title: 'Change this Position ID?',
        message:
          'IDAMAN users with the old Position ID are removed and can no longer sign in. Users with the new Position ID are pulled at the next sync.',
        subject: `${formData.originalPositionId} → ${formData.positionId.trim()}`,
        confirmLabel: 'Change Position ID',
      });
      if (!confirmed) return;
    }

    setModalLoading(true);
    try {
      const payload = {
        positionId: formData.positionId.trim(),
        name: formData.name,
        department: formData.department,
        description: formData.description,
      };

      const url = isEdit
        ? `${api.API_URL}/position-idaman/${formData.id}`
        : `${api.API_URL}/position-idaman`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await readError(response, 'Save operation failed');
      }

      const result = await response.json().catch(() => ({}));
      notify.success(
        `Position ID ${isEdit ? 'updated' : 'created'} successfully.${removedUsersText(result.data?.removedUsers)}`
      );
      toggleModal();
      fetchData();
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      id: 'positionId',
      name: 'Position ID',
      selector: row => row.positionId,
      sortable: true,
      minWidth: '160px',
      cell: row => (
        <code className='to-title' title={String(row.positionId ?? '')}>
          {row.positionId}
        </code>
      ),
    },
    {
      id: 'name',
      name: 'Position Name',
      selector: row => row.name,
      sortable: true,
      minWidth: '200px',
      cell: row => <span>{row.name || '-'}</span>,
    },
    {
      id: 'department',
      name: 'Department',
      selector: row => row.department,
      sortable: true,
      minWidth: '200px',
      cell: row => <span>{row.department || '-'}</span>,
    },
    {
      name: 'Description',
      selector: row => row.description,
      minWidth: '240px',
      cell: row => (
        <span className='text-muted' title={String(row.description ?? '')}>
          {row.description || '-'}
        </span>
      ),
    },
    {
      name: 'IDAMAN Users',
      selector: row => row.userCount,
      width: '130px',
      right: true,
      cell: row => (
        <Link
          to={`/apps-idaman-user/view?positionId=${encodeURIComponent(row.positionId)}`}
          title='Show IDAMAN users with this Position ID'
        >
          {row.userCount ?? 0}
        </Link>
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '100px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleEdit(row)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDelete(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
        <div className='email-content minimal-border'>
          <div className='p-4'>
            <div className='to-hero mb-4'>
              <PageBreadcrumb fallback={{ label: 'Position ID IDAMAN' }} />

              <div className='d-flex align-items-center gap-3 flex-wrap'>
                <span className='to-hero-icon'>
                  <i className='ri-profile-line' aria-hidden='true' />
                </span>
                <div className='flex-grow-1'>
                  <h4 className='to-hero-title'>Position ID IDAMAN</h4>
                  <p className='to-hero-subtitle'>
                    Only IDAMAN users of company 2222 holding these Position IDs
                    are pulled daily at 05:00 WIB and given a login with the
                    General role.
                  </p>
                </div>
                <div className='d-flex gap-2 flex-wrap'>
                  <button
                    type='button'
                    className='to-hero-btn'
                    onClick={() => fetchData()}
                  >
                    <i className='ri-refresh-line' aria-hidden='true' />
                    Refresh
                  </button>
                  <button
                    type='button'
                    className='to-hero-btn'
                    onClick={handleSync}
                    disabled={isSyncing || totalRows === 0}
                  >
                    <i
                      className='ri-download-cloud-2-line'
                      aria-hidden='true'
                    />
                    {isSyncing ? 'Starting sync...' : 'Sync Now'}
                  </button>
                  <button
                    type='button'
                    className='to-hero-btn to-hero-btn--solid'
                    onClick={toggleModal}
                  >
                    <i className='ri-add-line' aria-hidden='true' />
                    Add Position ID
                  </button>
                </div>
              </div>
            </div>

            <div className='to-panel'>
              <div className='to-toolbar'>
                <ListSearch
                  value={searchText}
                  onChange={handleSearch}
                  placeholder='Search position ID, name, department, or description...'
                  ariaLabel='Search position IDs'
                />
                <ClearFiltersButton
                  count={filterChips.length}
                  onClick={clearFilters}
                />
                <span className='to-toolbar-meta'>
                  {isLoading
                    ? 'Loading...'
                    : `Showing ${startEntry}–${endEntry} of ${totalRows} position IDs`}
                </span>
              </div>

              <ActiveFilterChips chips={filterChips} />

              <div className='to-table-wrap'>
                <DataTable
                  columns={columns}
                  data={data.items}
                  onSort={(column, dir) => {
                    setSortField(column.id);
                    setSortDirection(dir);
                    setCurrentPage(1);
                  }}
                  sortServer
                  highlightOnHover
                  persistTableHead
                  progressPending={isLoading}
                  progressComponent={<SkeletonRows />}
                  noDataComponent={
                    <EmptyState
                      title='No position ID found'
                      emptyHint='No Position ID is registered yet, so no IDAMAN user will be pulled. Add the Position IDs whose holders may sign in.'
                      filteredHint={`No position ID matches ${filterChips
                        .map(chip => `${chip.label} "${chip.value}"`)
                        .join(
                          ', '
                        )}. Try a different keyword or clear the filters.`}
                      hasFilters={hasFilters}
                      onClear={clearFilters}
                      action={{
                        label: 'Add Position ID',
                        onClick: toggleModal,
                      }}
                    />
                  }
                  customStyles={{
                    headCells: {
                      style: { paddingLeft: '12px', paddingRight: '12px' },
                    },
                    cells: {
                      style: {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        wordBreak: 'break-word',
                      },
                    },
                    table: { style: { width: '100%' } },
                  }}
                />
              </div>

              <ListPager
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={totalRows}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePerRowsChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        size='lg'
        backdrop='static'
        centered
        onClosed={() => setFormData(initialFormState)}
      >
        <ModalHeader toggle={toggleModal} className='bg-light'>
          {formData.id ? 'Edit Position ID' : 'Add Position ID'}
        </ModalHeader>
        <ModalBody>
          <RequiredNote />
          <FormGrid columns={1}>
            <TextField
              label='Position ID'
              name='positionId'
              required
              placeholder='e.g. 30012345'
              hint='Position ID exactly as it appears in IDAMAN'
              value={formData.positionId}
              error={errors.positionId}
              onChange={e => {
                setFormData(prev => ({
                  ...prev,
                  positionId: e.target.value,
                }));
                if (errors.positionId)
                  setErrors(prev => ({ ...prev, positionId: undefined }));
              }}
            />
            <TextField
              label='Position Name'
              name='name'
              placeholder='e.g. Manager Crude Oil Procurement'
              value={formData.name}
              error={errors.name}
              onChange={e => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
                if (errors.name)
                  setErrors(prev => ({ ...prev, name: undefined }));
              }}
            />
            <TextField
              label='Department'
              name='department'
              placeholder='e.g. Crude Oil Procurement'
              value={formData.department}
              error={errors.department}
              onChange={e => {
                setFormData(prev => ({ ...prev, department: e.target.value }));
                if (errors.department)
                  setErrors(prev => ({ ...prev, department: undefined }));
              }}
            />
            <TextAreaField
              label='Description'
              name='description'
              placeholder='e.g. Function that uses Hydro for crude tenders'
              value={formData.description}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </FormGrid>
        </ModalBody>
        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={toggleModal}
            disabled={modalLoading}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-save-line'
            onClick={handleSave}
            loading={modalLoading}
          >
            {formData.id ? 'Save Changes' : 'Add Position ID'}
          </ActionButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PositionIdamanView;
